import { Field, FieldProps, FormikProps } from 'formik';
import { FormComponent } from '@synergy/abstract';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Grid, Theme } from '@mui/material';
import { Media } from '@synergy/models';
import { PaloLinkRow } from '@synergy/components-palo';
import { PoolDropzone, PoolFileList, PoolTextArea, PoolTextField, PoolTypography } from '@synergy/components';
import { UploadState, UploadType } from '@synergy/models';
import { WithStyles, createStyles, withStyles } from '@mui/styles';

import { EditOffer } from '../model/edit-offer';
import { OfferFooterConditions } from './offers-footer-conditions';

const styles = (theme: Theme) =>
    createStyles({
        container: {
            paddingRight: theme.spacing(3),
        },
        delivery: {
            '& > div > input': {
                textAlign: 'right',
                paddingRight: 5,
            },
        },
        summaryLeft: {
            padding: theme.spacing(0, 3),
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
        },
        summaryRight: {
            padding: theme.spacing(2, 0),
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            minHeight: theme.spacing(7),
        },
        boxesLeft: {
            paddingRight: theme.spacing(),
        },
        boxesRight: {
            paddingLeft: theme.spacing(),
        },
        totalSumHighlight: {
            backgroundColor: '#F1F1F1',
        },
        padRight: {
            marginRight: 20,
        },
    });

interface OfferEditFormFooterProps extends WithStyles<typeof styles> {
    formikProps: FormikProps<EditOffer>;
    currency: string;
    mediaBaseUrl: string;
    readonly: boolean;
}

export const OfferEditFormFooter = withStyles(styles)(
    class OfferEditFormFooterBase extends FormComponent<EditOffer, OfferEditFormFooterProps, Record<string, unknown>> {
        private handleFileDrop = (acceptedFiles: File[]) => {
            const existingMedia = this.props.formikProps.values.media;

            const deduplicated = acceptedFiles.filter(
                acceptedFile =>
                    !existingMedia.some(
                        media => media.originalName === acceptedFile.name && media.size === acceptedFile.size
                    )
            );

            const newMedia = deduplicated.map(file => Media.fromFile(file, UploadType.Internal));
            newMedia.forEach(media => (media.state = UploadState.Finished));
            this.props.formikProps.setFieldValue('media', existingMedia.concat(newMedia));
        };

        private handleFileRemove = (media: Media) => {
            const existingMedia = this.props.formikProps.values.media;

            const index = existingMedia.indexOf(media);
            if (index >= 0) {
                const newMedia = [...existingMedia];
                newMedia.splice(index, 1);
                this.props.formikProps.setFieldValue('media', newMedia);
            }
        };

        public render() {
            const { classes, formikProps, currency, mediaBaseUrl } = this.props;

            const offerId = formikProps.values.id;
            const total =
                formikProps.values.offerItems.reduce(
                    (sum, offerItem) =>
                        offerItem.isOffered || offerItem.isAlternative
                            ? sum + (offerItem.price || 0) * offerItem.quantity
                            : sum,
                    0
                ) +
                formikProps.values.deliveryPrice +
                formikProps.values.surcharge;

            const supplierFiles = formikProps.values.media.filter(media => media.type === UploadType.Supplier);
            const internalFiles = formikProps.values.media.filter(media => media.type === UploadType.Internal);

            return (
                <div className={classes.container}>
                    <Grid
                        container={true}
                        spacing={2}
                        direction="column"
                        alignItems="flex-end"
                        sx={{ width: 350, ml: 'auto', mb: 4 }}
                    >
                        <Grid item={true} container={true} xs={3} direction="row">
                            <Grid item={true} xs={8} className={classes.summaryLeft}>
                                <PoolTypography id="app.label.deliverCosts" />
                            </Grid>
                            <Grid item={true} xs={4} className={classes.summaryRight}>
                                <Field name="deliveryPrice">
                                    {({ field, form }: FieldProps<EditOffer>) => (
                                        <PoolTextField
                                            className={classes.delivery}
                                            fieldType="fullWidth"
                                            isNumeric={true}
                                            minDecimalScale={2}
                                            {...field}
                                            error={this.hasError(form, field.name)}
                                            helperText={this.getError(form, field.name)}
                                            readOnly={this.props.readonly}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={8} className={classes.summaryLeft}>
                                <PoolTypography id="app.label.surcharges" />
                            </Grid>
                            <Grid item={true} xs={4} className={classes.summaryRight}>
                                <Field name="surcharge">
                                    {({ field, form }: FieldProps<EditOffer>) => (
                                        <PoolTextField
                                            className={classes.delivery}
                                            fieldType="fullWidth"
                                            isNumeric={true}
                                            minDecimalScale={2}
                                            {...field}
                                            error={this.hasError(form, field.name)}
                                            helperText={this.getError(form, field.name)}
                                            readOnly={this.props.readonly}
                                        />
                                    )}
                                </Field>
                            </Grid>
                            <Grid item={true} xs={8} className={`${classes.summaryLeft} ${classes.totalSumHighlight}`}>
                                <PoolTypography id="app.label.total" />
                            </Grid>
                            <Grid item={true} xs={4} className={`${classes.summaryRight} ${classes.totalSumHighlight}`}>
                                <PoolTypography className={classes.padRight}>
                                    <FormattedNumber value={total} currency={currency} style="currency" />
                                </PoolTypography>
                            </Grid>
                        </Grid>
                    </Grid>

                    <OfferFooterConditions bonusReadonly={false} readonly={this.props.readonly} />

                    <PoolTypography
                        styleType="sectionTitle"
                        id={
                            formikProps.values.supplierDeclinedReason
                                ? 'app.header.declinedReason'
                                : 'app.header.noteFromSupplier'
                        }
                        sx={{ pt: 2 }}
                    />
                    <Grid container={true} spacing={3} sx={{ mb: 4 }}>
                        <Grid item={true} lg={7} xs={12}>
                            {formikProps.values.supplierDeclinedReason ? (
                                <Field name="supplierDeclinedReason">
                                    {({ field }: FieldProps<string>) => (
                                        <PoolTextArea
                                            label={<FormattedMessage id="app.common.reason" />}
                                            rows="5"
                                            rowsMax="50"
                                            value={field.value}
                                            readOnly={true}
                                        />
                                    )}
                                </Field>
                            ) : (
                                <Field name="noteFromSupplier">
                                    {({ field }: FieldProps<string>) => (
                                        <PoolTextArea
                                            label={<FormattedMessage id="app.label.note" />}
                                            rows="5"
                                            rowsMax="50"
                                            value={field.value}
                                            readOnly={true}
                                        />
                                    )}
                                </Field>
                            )}
                        </Grid>
                        <Grid item={true} lg={5} xs={12}>
                            <PoolFileList acceptedFiles={supplierFiles} downloadBaseUrl={mediaBaseUrl} />
                        </Grid>
                    </Grid>

                    <PoolTypography styleType="sectionTitle" id="app.header.internalNote" />
                    <Grid container={true} spacing={2}>
                        <Grid item={true} lg={7} xs={12}>
                            <Field name="internalNote">
                                {({ field }: FieldProps<EditOffer>) => (
                                    <>
                                        <PoolTextArea
                                            label={<FormattedMessage id="app.label.note" />}
                                            rows="5"
                                            rowsMax="50"
                                            {...field}
                                        />
                                        <PaloLinkRow text={String(field.value ?? '')} />
                                    </>
                                )}
                            </Field>
                        </Grid>
                        <Grid container={true} item={true} lg={5} xs={12} spacing={3}>
                            <Grid item={true} xs={12}>
                                <PoolDropzone onDrop={this.handleFileDrop} disabled={!offerId}>
                                    {!offerId && <FormattedMessage id="app.upload.saveBeforeDrop" />}
                                </PoolDropzone>
                            </Grid>
                            <Grid item={true} xs={12}>
                                <PoolFileList
                                    acceptedFiles={internalFiles}
                                    onDeleteClicked={this.handleFileRemove}
                                    downloadBaseUrl={mediaBaseUrl}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </div>
            );
        }
    }
);
